/**
 * useAnalytics Hook
 *
 * Provides easy access to Google Analytics tracking functions
 * Automatically includes user authentication status in all events
 */

import { useAuth } from '../../features/auth';
import {
  trackEvent,
  trackCalculatorStarted,
  trackCalculatorCompleted,
  trackResultsViewed,
  trackCalculationSaved,
  trackCTAClick,
  trackTariffCompared,
  trackBaselineChanged,
  trackDetailedComparisonViewed,
  GAEvent,
  type UserStatus,
} from '../../utils/analytics';

export function useAnalytics() {
  const { isAuthenticated } = useAuth();

  // Determine user status
  const userStatus: UserStatus = isAuthenticated ? 'authenticated' : 'anonymous';

  return {
    // Raw event tracking
    trackEvent: (eventName: GAEvent | string, params: Record<string, any> = {}) => {
      trackEvent(eventName, {
        user_status: userStatus,
        ...params,
      });
    },

    // Calculator events
    trackCalculatorStarted: () => trackCalculatorStarted(userStatus),

    trackCalculatorCompleted: (params: {
      current_tariff?: string;
      num_tariffs_compared?: number;
      potential_savings?: number;
    } = {}) => trackCalculatorCompleted(userStatus, params),

    trackResultsViewed: (params: {
      tariff_count?: number;
      has_savings?: boolean;
    } = {}) => trackResultsViewed(userStatus, params),

    // Engagement events
    trackCalculationSaved: trackCalculationSaved,

    // Navigation events
    trackCTAClick: (ctaName: string, destination?: string) =>
      trackCTAClick(ctaName, userStatus, destination),

    // Tariff comparison events
    trackTariffCompared: (currentTariff: string, selectedTariff: string) =>
      trackTariffCompared(currentTariff, selectedTariff, userStatus),

    trackBaselineChanged: (oldBaseline: string, newBaseline: string) =>
      trackBaselineChanged(oldBaseline, newBaseline, userStatus),

    trackDetailedComparisonViewed: (currentTariff: string, recommendedTariff: string) =>
      trackDetailedComparisonViewed(currentTariff, recommendedTariff, userStatus),

    // User status getter
    userStatus,
    isAuthenticated,
  };
}
